import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button } from '@roger/ui-components';
import { ArrowLeft, CheckCircle, Pencil, ShieldCheck } from 'lucide-react';
import { OutsideEpisodeBadge } from '../episode/OutsideEpisodeBadge';
import {
  TaskHeaderControls,
  TaskWorkflowStatusBadge,
  type TaskHeaderSecondaryAction,
} from '../task/TaskHeaderControls';
import { formatDate } from '../task/TaskDetailPanel';
import type {
  TaskSignatureBypassResponse,
  TaskSignatureResponse,
  VisitResponse,
} from '../../services/api/types.gen';
import type { SigningAttestation } from '../signing/attestations';

const formatDateTime = (dateString: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

export interface VisitDetailHeaderProps {
  visit: VisitResponse;
  patientId: string;
  isMissedVisit: boolean;
  onBack?: () => void;
  signatures: TaskSignatureResponse[];
  bypasses: TaskSignatureBypassResponse[];
  hasPrimaryHeaderActions: boolean;
  canBreakSignatures: boolean;
  canSign: boolean;
  canApprove: boolean;
  isReadonly: boolean;
  editButtonLabel: string;
  signButtonLabel: string;
  editingLoading: boolean;
  signingLoading: boolean;
  approvingLoading: boolean;
  /** The chart's first OASIS scrub is still loading. */
  oasisCheckPending: boolean;
  attestationsLoading: boolean;
  visitAttestation: SigningAttestation | null;
  hasStagedAnswers: boolean;
  stagedAnswersTooltip?: string;
  onBreakSignatures: () => void;
  onSign: () => void;
  onApprove: () => void;
  secondaryActions: TaskHeaderSecondaryAction[];
}

/** Visit chart chrome: back link, title/badges, and primary + Actions menu. */
export const VisitDetailHeader: React.FC<VisitDetailHeaderProps> = ({
  visit,
  patientId,
  isMissedVisit,
  onBack,
  signatures,
  bypasses,
  hasPrimaryHeaderActions,
  canBreakSignatures,
  canSign,
  canApprove,
  isReadonly,
  editButtonLabel,
  signButtonLabel,
  editingLoading,
  signingLoading,
  approvingLoading,
  oasisCheckPending,
  attestationsLoading,
  visitAttestation,
  hasStagedAnswers,
  stagedAnswersTooltip,
  onBreakSignatures,
  onSign,
  onApprove,
  secondaryActions,
}) => {
  const navigate = useNavigate();

  return (
    <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 border-b bg-muted/20'>
      <div className='flex min-w-0 flex-1 items-center gap-3'>
        <Button
          variant='outline'
          size='sm'
          onClick={() =>
            onBack ? onBack() : navigate(`/patients/${patientId}`)
          }
          className='flex items-center gap-1.5 flex-shrink-0'
        >
          <ArrowLeft className='h-3.5 w-3.5' />
          See All Visits
        </Button>
        <div className='h-4 w-px bg-border' />
        <div className='min-w-0 flex-1'>
          <div className='flex min-w-0 items-center gap-2'>
            <span className='min-w-0 truncate text-sm font-medium text-foreground'>
              {visit.visit_type_display_name}
            </span>
            {isMissedVisit && (
              <Badge
                variant='destructive'
                className='shrink-0 text-[10px] px-1.5 py-0'
              >
                Missed
              </Badge>
            )}
            {visit.order_not_required && (
              <Badge
                variant='secondary'
                className='shrink-0 whitespace-nowrap text-[10px] px-1.5 py-0'
              >
                No order required
              </Badge>
            )}
            <OutsideEpisodeBadge
              episodeStartDate={visit.episode_start_date}
              episodeEndDate={visit.episode_end_date}
              targetDate={visit.scheduled_date}
              visitDate={visit.visit_date}
            />
          </div>
          <div className='text-xs text-muted-foreground'>
            Visit date: {formatDate(visit.visit_date)} · Last updated:{' '}
            {visit.visit_data_last_updated
              ? formatDateTime(visit.visit_data_last_updated)
              : 'Never'}
          </div>
        </div>
      </div>

      <TaskHeaderControls
        className='w-full sm:w-auto'
        statusItems={
          <TaskWorkflowStatusBadge
            status={visit.status}
            signatures={signatures}
            bypasses={bypasses}
            missedVisitData={visit.missed_visit_data}
          />
        }
        primaryActions={
          hasPrimaryHeaderActions ? (
            <>
              {canBreakSignatures && !isReadonly && (
                <Button
                  onClick={onBreakSignatures}
                  disabled={editingLoading}
                  size='sm'
                  variant='outline'
                  className='flex items-center gap-1.5'
                >
                  {editingLoading ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Pencil className='h-4 w-4' />
                      {editButtonLabel}
                    </>
                  )}
                </Button>
              )}

              {canSign && (
                <Button
                  onClick={onSign}
                  disabled={
                    signingLoading ||
                    oasisCheckPending ||
                    attestationsLoading ||
                    !visitAttestation ||
                    hasStagedAnswers
                  }
                  title={stagedAnswersTooltip}
                  size='sm'
                  variant='default'
                  className='flex items-center gap-1.5'
                >
                  {signingLoading || oasisCheckPending ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                      {signingLoading ? 'Signing...' : 'Checking chart...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle className='h-4 w-4' />
                      {signButtonLabel}
                    </>
                  )}
                </Button>
              )}

              {canApprove && (
                <Button
                  onClick={onApprove}
                  disabled={
                    approvingLoading ||
                    oasisCheckPending ||
                    attestationsLoading ||
                    !visitAttestation ||
                    hasStagedAnswers
                  }
                  title={stagedAnswersTooltip}
                  size='sm'
                  variant='default'
                  className='flex items-center gap-1.5'
                >
                  {approvingLoading || oasisCheckPending ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                      {approvingLoading ? 'Completing...' : 'Checking chart...'}
                    </>
                  ) : (
                    <>
                      <ShieldCheck className='h-4 w-4' />
                      Sign & Complete
                    </>
                  )}
                </Button>
              )}
            </>
          ) : undefined
        }
        secondaryActions={secondaryActions}
      />
    </div>
  );
};
